import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {plainToClass, plainToClassFromExist} from 'class-transformer';
import {environment} from '../../../environments/environment';
import {PlaceApiModel} from '../models/place.api-model';
import {PlaceCategoryApiModel} from '../models/place-category.api-model';
import {ResultListApiModel} from '../models/result-list.api.model';
import {PlaceCheckInApiModel} from '../models/place-check-in.api-model';

export interface PlaceQuery {
  name?: string;
  categories?: string[];
  parentCategories?: string[];
  latitude?: number;
  longitude?: number;
  radius?: number;
  page?: number;
  limit?: number;
  pagination?: boolean;
  status?: string;
}

export interface PlaceRatingResponse {
  note: number;
  averageRating: number;
  ratingsCount: number;
}

export interface PlaceTranslationSuggestion {
  sourceLanguage: string;
  translations: Record<'pl-PL'|'en-US'|'de-DE', string>;
}

@Injectable({providedIn: 'root'})
export class PlaceRestService {
  constructor(private readonly http: HttpClient) {}
  getCollection(query: PlaceQuery = {}): Observable<ResultListApiModel<PlaceApiModel>> {
    let params = new HttpParams();
    if (query.name) params = params.set('name', query.name);
    query.categories?.forEach(category => params = params.append('categories[]', category));
    query.parentCategories?.forEach(category => params = params.append('parentCategories[]', category));
    if (Number.isFinite(query.latitude) && Number.isFinite(query.longitude)) {
      params = params.set('order[distance]', `${query.latitude},${query.longitude}`);
      if (query.radius) params = params.set('address.coordinates', `${query.latitude},${query.longitude},${query.radius}`);
    }
    if (query.page && query.page > 1) params = params.set('page', query.page.toString());
    if (query.limit && query.limit > 0) params = params.set('limit', query.limit.toString());
    if (query.pagination === false) params = params.set('pagination', 'false');
    if (query.status) params = params.set('status', query.status);
    return this.http.get<ResultListApiModel<PlaceApiModel>>(`${environment.apiUrl}/places`, {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<PlaceApiModel>(PlaceApiModel), result as object)));
  }

  /** Follows the API Platform cursor verbatim so every pagination parameter is retained. */
  getCollectionByCursor(cursor: string): Observable<ResultListApiModel<PlaceApiModel>> {
    const url = cursor.startsWith('http') ? cursor : `${environment.apiUrl.replace(/\/api$/, '')}${cursor}`;
    return this.http.get<ResultListApiModel<PlaceApiModel>>(url)
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<PlaceApiModel>(PlaceApiModel), result as object)));
  }

  get(slug: string, refresh = false): Observable<PlaceApiModel> {
    const headers = refresh ? new HttpHeaders({'X-Cache-Refresh': 'true'}) : undefined;
    return this.http.get<PlaceApiModel>(`${environment.apiUrl}/places/${slug}`, {headers})
      .pipe(map(value => plainToClass(PlaceApiModel, value)));
  }
  getCheckIns(place: PlaceApiModel, page = 1, limit = 20): Observable<ResultListApiModel<PlaceCheckInApiModel>> {
    const params = new HttpParams().set('page', page.toString()).set('limit', limit.toString());
    return this.http.get<ResultListApiModel<PlaceCheckInApiModel>>(`${environment.apiUrl}/places/${place.slug}/check-ins`, {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<PlaceCheckInApiModel>(PlaceCheckInApiModel), result as object)));
  }
  getCategories(): Observable<PlaceCategoryApiModel[]> {
    return this.http.get<PlaceCategoryApiModel[]>(`${environment.apiUrl}/place_categories`).pipe(
      map(value => (value as any)['hydra:member'] || value),
      // Accept the short-lived, previously emitted method-shaped field too so
      // a client refreshed during deployment still sees the correct tree.
      map(value => value.map((item: any) => plainToClass(PlaceCategoryApiModel, {
        ...item,
        parentSlug: item.parentSlug ?? item.getParentSlug,
      }))),
      map(categories => categories.sort((left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name))),
    );
  }
  suggestNameTranslations(name: string, sourceLanguage?: string): Observable<PlaceTranslationSuggestion> {
    return this.http.post<PlaceTranslationSuggestion>(`${environment.apiUrl}/places/translation-suggestions`, {name, sourceLanguage});
  }
  create(payload: object): Observable<PlaceApiModel> { return this.http.post<PlaceApiModel>(`${environment.apiUrl}/places`, payload).pipe(map(value => plainToClass(PlaceApiModel, value))); }
  resubmit(slug: string, payload: object): Observable<PlaceApiModel> { return this.http.put<PlaceApiModel>(`${environment.apiUrl}/places/${slug}/resubmit`, payload).pipe(map(value => plainToClass(PlaceApiModel, value))); }
  dismissRejectionNotice(slug: string): Observable<PlaceApiModel> { return this.http.post<PlaceApiModel>(`${environment.apiUrl}/places/${slug}/dismiss-rejection-notice`, {}).pipe(map(value => plainToClass(PlaceApiModel, value))); }
  moderate(slug: string, status: 'PUBLISHED'|'CHANGES_REQUESTED'|'REJECTED', reviewIssues: string[] = []): Observable<PlaceApiModel> {
    return this.http.post<PlaceApiModel>(`${environment.apiUrl}/places/${slug}/moderate`, {status, reviewIssues})
      .pipe(map(value => plainToClass(PlaceApiModel, value)));
  }
  getAdminCollection(query: PlaceQuery = {}): Observable<ResultListApiModel<PlaceApiModel>> {
    let params = new HttpParams();
    if (query.name) params = params.set('name', query.name);
    if (query.status) params = params.set('status', query.status);
    if (query.page) params = params.set('page', query.page.toString());
    if (query.limit) params = params.set('limit', query.limit.toString());
    return this.http.get<ResultListApiModel<PlaceApiModel>>(`${environment.apiUrl}/admin/places`, {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<PlaceApiModel>(PlaceApiModel), result as object)));
  }
  getAdmin(slug: string): Observable<PlaceApiModel> { return this.http.get<PlaceApiModel>(`${environment.apiUrl}/admin/places/${slug}`).pipe(map(value => plainToClass(PlaceApiModel, value))); }
  createAdmin(payload: object): Observable<PlaceApiModel> { return this.http.post<PlaceApiModel>(`${environment.apiUrl}/admin/places`, payload).pipe(map(value => plainToClass(PlaceApiModel, value))); }
  updateAdmin(slug: string, payload: object): Observable<PlaceApiModel> { return this.http.put<PlaceApiModel>(`${environment.apiUrl}/admin/places/${slug}`, payload).pipe(map(value => plainToClass(PlaceApiModel, value))); }
  deleteAdmin(slug: string): Observable<void> { return this.http.delete<void>(`${environment.apiUrl}/admin/places/${slug}`); }
  checkIn(place: PlaceApiModel, coordinates?: {lat: number; lng: number}): Observable<{locationVerified: boolean}> { return this.http.post<{locationVerified: boolean}>(`${environment.apiUrl}/places/${place.slug}/check-in`, coordinates ? {latitude: coordinates.lat, longitude: coordinates.lng} : {}); }
  rate(place: PlaceApiModel, note: number): Observable<PlaceRatingResponse> {
    return this.http.post<PlaceRatingResponse>(`${environment.apiUrl}/places/${place.slug}/rate`, {note});
  }
}
